import { createAsyncThunk, AnyAction } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import i18n from "config/i18n"
import { API } from "redux/api"
import {
  ACTION_STATUS_SUCCESS,
  POLLING_INTERVAL,
  POLLING_TIMEOUT,
} from "config"
import {
  start as startPolling,
  reset as resetPolling,
} from "redux/features/polling/pollingSlice"
import {
  SubscriptionPaymentValues,
  PaymentThunkResponseType,
  SubscriptionRequestParamsType,
} from "types"

export const createPaymentRequestSession = createAsyncThunk(
  "subscription-payment-request-session/create",
  async (values: SubscriptionPaymentValues) => {
    const response = await API.post("/subscription_payment_requests", values)
    return response.data
  },
)

export const fetchSubscriptionPaymentRequestSession = createAsyncThunk(
  "subscription-payment-request-session/fetch",
  async (values: { session_id: string }) => {
    const response = await API.get(
      `/subscription_payment_requests/session/${values.session_id}`,
    )

    return response.data
  },
)

export const makeSubscriptionPayment = (values: SubscriptionPaymentValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise<{
      request_uri?: string
      request_method?: string
      request_params?: SubscriptionRequestParamsType
    }>((resolve, reject) => {
      return dispatch(createPaymentRequestSession(values))
        .then(
          ({ payload }) => {
            if (!payload || !payload.subscription_payment_request) {
              return reject(payload.error)
            }

            let payment_result: PaymentThunkResponseType

            const timerId = setInterval(async () => {
              const { session_id } = payload.subscription_payment_request
              const result = await dispatch(
                fetchSubscriptionPaymentRequestSession({ session_id }),
              )

              if (result?.payload?.session) {
                const { status, action_payload } = result.payload.session

                // NOTE: clear interval when status = success
                if (status === ACTION_STATUS_SUCCESS) {
                  clearInterval(timerId)
                  dispatch(resetPolling())

                  const { request_uri, request_params, request_method } =
                    action_payload

                  payment_result = {
                    request_uri,
                    request_method,
                    request_params,
                  }

                  resolve(payment_result)
                }
              } else {
                dispatch(resetPolling())
                clearInterval(timerId)

                reject(i18n.t("notice.payment.session.unavailable"))
              }
            }, POLLING_INTERVAL)

            dispatch(startPolling({ pollingId: timerId }))

            setTimeout(() => {
              clearInterval(timerId)
              dispatch(resetPolling())
              if (!payment_result) {
                reject(i18n.t("notice.payment.create.failure"))
              }
            }, POLLING_TIMEOUT)
          },
          () => {
            reject(i18n.t("notice.payment.session.failure"))
          },
        )
        .catch(() => {
          reject(i18n.t("notice.payment.session.failure"))
        })
    })
  }
}
